import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import {
  FiActivity,
  FiArrowRight,
  FiAward,
  FiCheckCircle,
  FiHeart,
  FiRefreshCw,
  FiShield,
  FiTrendingUp,
  FiUsers,
  FiZap
} from 'react-icons/fi';
import { getImageUrl } from '../utils/api';
import Slider from '../components/Slider';
import HomeProductCard from '../components/HomeProductCard';
import { useFavorites } from '../contexts/FavoritesContext';
import { useAuth } from '../contexts/AuthContext';

const GOALS = [
  {
    icon: FiTrendingUp,
    number: '01',
    title: 'Набор массы',
    description: 'Гейнеры, протеин и продукты для калорийного рациона.',
    tone: 'charcoal'
  },
  {
    icon: FiZap,
    number: '02',
    title: 'Энергия',
    description: 'Аминокислоты и функциональные напитки для активного дня.',
    tone: 'terracotta'
  },
  {
    icon: FiRefreshCw,
    number: '03',
    title: 'Восстановление',
    description: 'Продукты для поддержки мышц после нагрузки.',
    tone: 'ivory'
  },
  {
    icon: FiHeart,
    number: '04',
    title: 'Баланс',
    description: 'Витамины, травяные напитки и ежедневная поддержка.',
    tone: 'sage'
  }
];

const TRUST_ITEMS = [
  { icon: FiActivity, title: 'Своё производство', text: 'Спортивное питание выпускается в Самарской области' },
  { icon: FiShield, title: 'Документы на продукцию', text: 'Сертификаты и декларации собраны в открытом разделе сайта' },
  { icon: FiAward, title: 'Контроль каждой партии', text: 'Проверяем сырьё, смешивание, фасовку и готовую продукцию' },
  { icon: FiUsers, title: 'Экспертные подборки', text: 'Продуктовые комплексы от тренеров и нутрициологов' }
];

const PRODUCT_LINES = [
  {
    icon: FiActivity,
    number: '01',
    title: 'Протеины',
    text: 'Сывороточные, соевые, казеиновые и многокомпонентные смеси.'
  },
  {
    icon: FiTrendingUp,
    number: '02',
    title: 'Гейнеры, коктейли и мюсли',
    text: 'Питательные гейнеры, молочные коктейли и обогащённые мюсли.'
  },
  {
    icon: FiHeart,
    number: '03',
    title: 'Витамины и функциональные напитки',
    text: 'Магний, омега-3, витаминно-минеральные комплексы и травяные напитки.'
  }
];

const STORY_FACTS = [
  'Контроль сырья перед запуском в производство',
  'Проверка смешивания, фасовки и готовой партии',
  'Экспертная оценка с участием профильных специалистов'
];

const HomeContainer = styled.main`
  --home-bg: #f6f2ea;
  --home-surface: #ffffff;
  --home-ink: #20211e;
  --home-muted: #666961;
  --home-line: #ddd8ce;
  --home-accent: #b65a3a;
  --home-accent-dark: #8f432d;
  --home-accent-soft: #f2e5de;
  --home-sage: #657456;
  --home-sage-soft: #e8ecdf;
  --home-charcoal: #222521;

  min-height: 100vh;
  overflow-x: hidden;
  background:
    radial-gradient(circle at 8% 24%, rgba(182, 90, 58, .07), transparent 28rem),
    var(--home-bg);
  color: var(--home-ink);
`;

const TrustSection = styled.section`
  position: relative;
  z-index: 4;
  width: min(1240px, calc(100% - 48px));
  margin: -28px auto 0;

  @media (max-width: 700px) {
    width: 100%;
    margin-top: 0;
  }
`;

const TrustGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  overflow: hidden;
  border: 1px solid rgba(32, 33, 30, .09);
  border-radius: 16px;
  background: rgba(255, 255, 255, .96);
  box-shadow: 0 20px 52px rgba(39, 34, 28, .11);

  @media (max-width: 920px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 700px) {
    border-right: 0;
    border-left: 0;
    border-radius: 0;
    box-shadow: none;
  }
`;

const TrustItem = styled.div`
  min-height: 116px;
  padding: 24px;
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  gap: 14px;
  align-items: center;
  border-right: 1px solid #e8e3d9;

  &:last-child { border-right: 0; }

  svg {
    width: 24px;
    height: 24px;
    color: var(--home-accent);
  }

  strong {
    display: block;
    margin-bottom: 5px;
    font-size: 14px;
    line-height: 1.3;
  }

  span {
    display: block;
    color: #74766f;
    font-size: 12px;
    line-height: 1.45;
  }

  @media (max-width: 920px) {
    &:nth-child(2) { border-right: 0; }
    &:nth-child(-n + 2) { border-bottom: 1px solid #e8e3d9; }
  }

  @media (max-width: 540px) {
    min-height: 104px;
    padding: 18px 14px;
    grid-template-columns: 30px minmax(0, 1fr);
    gap: 9px;

    svg { width: 20px; height: 20px; }
    strong { font-size: 12px; }
    span { font-size: 10px; }
  }
`;

const Section = styled.section`
  width: min(1240px, calc(100% - 48px));
  margin: 0 auto;
  padding: clamp(76px, 8vw, 118px) 0;

  @media (max-width: 700px) {
    width: min(100% - 32px, 1240px);
    padding: 48px 0;
  }
`;

const SurfaceSection = styled.div`
  background: var(--home-surface);
`;

const SectionHead = styled.div`
  margin-bottom: 42px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 32px;

  @media (max-width: 720px) {
    margin-bottom: 30px;
    align-items: flex-start;
    flex-direction: column;
    gap: 18px;
  }
`;

const SectionIntro = styled.div`
  max-width: 740px;
`;

const Eyebrow = styled.p`
  margin: 0 0 14px;
  color: var(--home-accent-dark);
  font-size: 12px;
  line-height: 1.2;
  font-weight: 800;
  letter-spacing: .16em;
  text-transform: uppercase;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-family: var(--font-heading, 'BebasNeuePro', 'Arial Black', sans-serif);
  font-size: clamp(46px, 5.5vw, 76px);
  line-height: .94;
  letter-spacing: .02em;
  font-weight: 700;
  text-transform: uppercase;
`;

const SectionSubtitle = styled.p`
  max-width: 650px;
  margin: 20px 0 0;
  color: var(--home-muted);
  font-size: 17px;
  line-height: 1.65;

  @media (max-width: 600px) {
    font-size: 15px;
  }
`;

const InlineLink = styled(Link)`
  flex: 0 0 auto;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: var(--home-accent-dark);
  font-size: 14px;
  font-weight: 800;
  transition: gap 180ms ease, color 180ms ease;

  &:hover {
    gap: 14px;
    color: var(--home-ink);
  }

  &:focus-visible {
    outline: 3px solid rgba(182, 90, 58, .38);
    outline-offset: 4px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const GoalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 980px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`;

const getGoalBackground = tone => {
  if (tone === 'charcoal') return '#242722';
  if (tone === 'terracotta') return '#ead8cf';
  if (tone === 'sage') return '#e5eadc';
  return '#fbfaf7';
};

const getGoalColor = tone => tone === 'charcoal' ? '#fff' : '#20211e';

const GoalCard = styled(Link)`
  position: relative;
  min-height: 310px;
  padding: 28px;
  overflow: hidden;
  border: 1px solid ${props => props.$tone === 'charcoal' ? '#242722' : 'rgba(32, 33, 30, .11)'};
  border-radius: 18px;
  display: flex;
  flex-direction: column;
  background: ${props => getGoalBackground(props.$tone)};
  color: ${props => getGoalColor(props.$tone)};
  transition: transform 220ms ease, box-shadow 220ms ease;

  &::after {
    content: '${props => props.$number}';
    position: absolute;
    top: 6px;
    right: 14px;
    color: currentColor;
    font-family: var(--font-heading, 'BebasNeuePro', sans-serif);
    font-size: 104px;
    line-height: 1;
    font-weight: 700;
    letter-spacing: -.04em;
    opacity: .075;
  }

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 24px 46px rgba(34, 31, 27, .14);
  }

  &:focus-visible {
    outline: 3px solid rgba(182, 90, 58, .42);
    outline-offset: 4px;
  }

  @media (max-width: 540px) {
    min-height: 250px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const GoalIcon = styled.span`
  width: 48px;
  height: 48px;
  border: 1px solid currentColor;
  border-radius: 50%;
  display: grid;
  place-items: center;
  opacity: .78;

  svg { width: 21px; height: 21px; }
`;

const GoalCopy = styled.div`
  position: relative;
  z-index: 1;
  margin-top: auto;

  h3 {
    margin: 0 0 12px;
    font-family: var(--font-heading, 'BebasNeuePro', 'Arial Black', sans-serif);
    font-size: 38px;
    line-height: 1;
    letter-spacing: .02em;
    text-transform: uppercase;
  }

  p {
    max-width: calc(100% - 56px);
    margin: 0;
    color: inherit;
    font-size: 14px;
    line-height: 1.55;
    opacity: .72;
  }
`;

const GoalArrow = styled.span`
  position: absolute;
  right: 24px;
  bottom: 24px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: ${props => props.$tone === 'charcoal' ? 'rgba(255,255,255,.12)' : 'rgba(32,33,30,.07)'};

  svg { width: 18px; height: 18px; }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 22px;

  @media (max-width: 940px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const shimmer = keyframes`
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
`;

const ProductSkeleton = styled.div`
  min-height: 500px;
  border: 1px solid #ebe7df;
  border-radius: 18px;
  background: linear-gradient(90deg, #f0eee9 25%, #faf9f6 50%, #f0eee9 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.3s infinite linear;

  @media (max-width: 560px) { min-height: 430px; }
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const Notice = styled.div`
  grid-column: 1 / -1;
  padding: 36px 24px;
  border: 1px solid var(--home-line);
  border-radius: 16px;
  background: #faf8f4;
  color: var(--home-muted);
  text-align: center;
  line-height: 1.6;
`;

const StorySection = styled.section`
  position: relative;
  width: min(1400px, calc(100% - 32px));
  min-height: 680px;
  margin: 20px auto 0;
  overflow: hidden;
  border-radius: 26px;
  display: flex;
  align-items: center;
  background: #24231f;
  color: #fff;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
    background: linear-gradient(90deg, rgba(28, 28, 25, .98) 0%, rgba(28, 28, 25, .91) 37%, rgba(28, 28, 25, .36) 66%, rgba(28, 28, 25, .12) 100%);
  }

  @media (max-width: 760px) {
    width: 100%;
    min-height: 0;
    border-radius: 0;
    display: block;

    &::after { display: none; }
  }
`;

const StoryImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;

  @media (max-width: 760px) {
    position: relative;
    height: 260px;
    object-position: 70% center;
  }
`;

const StoryContent = styled.div`
  position: relative;
  z-index: 2;
  width: min(1240px, calc(100% - 96px));
  margin: 0 auto;
  padding: 80px 0;

  @media (max-width: 760px) {
    width: 100%;
    padding: 48px 20px 56px;
    background: #24231f;
  }
`;

const StoryCopy = styled.div`
  max-width: 610px;

  h2 {
    max-width: 600px;
    margin: 0;
    font-family: var(--font-heading, 'BebasNeuePro', 'Arial Black', sans-serif);
    font-size: clamp(52px, 6vw, 82px);
    line-height: .92;
    letter-spacing: .02em;
    text-transform: uppercase;
  }

  > p {
    max-width: 570px;
    margin: 24px 0 0;
    color: rgba(255, 255, 255, .74);
    font-size: 17px;
    line-height: 1.65;
  }
`;

const LightEyebrow = styled(Eyebrow)`
  color: #d5a389;
`;

const StoryFacts = styled.div`
  max-width: 560px;
  margin-top: 32px;
  display: grid;
  gap: 10px;
`;

const StoryFact = styled.div`
  min-height: 56px;
  padding: 12px 16px;
  border: 1px solid rgba(255, 255, 255, .14);
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(255, 255, 255, .07);
  color: rgba(255, 255, 255, .88);
  font-size: 14px;

  svg {
    flex: 0 0 auto;
    width: 19px;
    height: 19px;
    color: #d5a389;
  }
`;

const ActionRow = styled.div`
  margin-top: 34px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;

  @media (max-width: 520px) {
    display: grid;
    grid-template-columns: 1fr;
  }
`;

const ActionLink = styled(Link)`
  min-height: 50px;
  padding: 0 20px;
  border: 1px solid ${props => props.$primary ? '#b65a3a' : 'rgba(255, 255, 255, .28)'};
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: ${props => props.$primary ? '#b65a3a' : 'transparent'};
  color: #fff;
  font-size: 14px;
  font-weight: 800;
  transition: transform 180ms ease, background 180ms ease;

  &:hover {
    transform: translateY(-2px);
    background: ${props => props.$primary ? '#98472f' : 'rgba(255, 255, 255, .09)'};
  }

  &:focus-visible {
    outline: 3px solid rgba(255, 255, 255, .72);
    outline-offset: 3px;
  }

  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 40px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const ProcessStep = styled.div`
  position: relative;
  min-height: 270px;
  padding: 28px 56px 24px 0;
  border-top: 1px solid #cfc8bc;

  &::after {
    content: '${props => props.$number}';
    position: absolute;
    top: 14px;
    right: 0;
    color: var(--home-ink);
    font-family: var(--font-heading, 'BebasNeuePro', sans-serif);
    font-size: 86px;
    line-height: 1;
    opacity: .07;
  }

  svg {
    width: 28px;
    height: 28px;
    color: var(--home-accent);
  }

  h3 {
    margin: 72px 0 12px;
    font-family: var(--font-heading, 'BebasNeuePro', 'Arial Black', sans-serif);
    font-size: 34px;
    line-height: 1;
    letter-spacing: .02em;
    text-transform: uppercase;
  }

  p {
    max-width: 320px;
    margin: 0;
    color: var(--home-muted);
    font-size: 15px;
    line-height: 1.6;
  }

  @media (max-width: 760px) {
    min-height: 220px;
  }
`;

const TasteSection = styled.section`
  position: relative;
  width: min(1400px, calc(100% - 32px));
  min-height: 680px;
  margin: 20px auto clamp(76px, 8vw, 118px);
  overflow: hidden;
  border-radius: 26px;
  display: flex;
  align-items: center;
  background: #211714;
  color: #fff;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
    background:
      radial-gradient(circle at 28% 70%, rgba(220, 118, 66, .24), transparent 30%),
      linear-gradient(90deg, rgba(30, 20, 17, .98) 0%, rgba(30, 20, 17, .91) 39%, rgba(30, 20, 17, .35) 67%, rgba(30, 20, 17, .08) 100%);
  }

  @media (max-width: 760px) {
    width: 100%;
    min-height: 0;
    border-radius: 0;
    display: block;

    &::after { display: none; }
  }
`;

const TasteImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;

  @media (max-width: 760px) {
    position: relative;
    height: 280px;
    object-position: 70% center;
  }
`;

const TasteContent = styled.div`
  position: relative;
  z-index: 2;
  width: min(1240px, calc(100% - 96px));
  margin: 0 auto;
  padding: 82px 0;

  @media (max-width: 760px) {
    width: 100%;
    padding: 50px 20px 58px;
    background:
      radial-gradient(circle at 10% 90%, rgba(220, 118, 66, .2), transparent 38%),
      #211714;
  }
`;

const TasteCopy = styled.div`
  max-width: 650px;

  h2 {
    margin: 0;
    font-family: var(--font-heading, 'BebasNeuePro', 'Arial Black', sans-serif);
    font-size: clamp(54px, 6vw, 86px);
    line-height: .92;
    letter-spacing: .02em;
    text-transform: uppercase;
  }

  > p {
    max-width: 570px;
    margin: 24px 0 0;
    color: rgba(255, 255, 255, .76);
    font-size: 17px;
    line-height: 1.65;
  }
`;

const FlavorList = styled.div`
  margin-top: 30px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const Flavor = styled.span`
  padding: 10px 14px;
  border: 1px solid rgba(255, 255, 255, .2);
  border-radius: 999px;
  background: rgba(255, 255, 255, .1);
  color: #fff;
  font-size: 12px;
  line-height: 1;
  font-weight: 800;
  letter-spacing: .06em;
  text-transform: uppercase;
  backdrop-filter: blur(8px);
`;

const CommunityPanel = styled.section`
  width: min(1240px, calc(100% - 48px));
  min-height: 610px;
  margin: 0 auto clamp(76px, 8vw, 118px);
  overflow: hidden;
  border-radius: 24px;
  display: grid;
  grid-template-columns: .9fr 1.1fr;
  background: var(--home-charcoal);
  color: #fff;
  box-shadow: 0 26px 62px rgba(35, 31, 26, .15);

  @media (max-width: 820px) {
    width: min(100% - 32px, 1240px);
    min-height: 0;
    grid-template-columns: 1fr;
  }
`;

const CommunityCopy = styled.div`
  padding: clamp(40px, 6vw, 76px);
  display: flex;
  flex-direction: column;
  justify-content: center;

  h2 {
    margin: 0;
    font-family: var(--font-heading, 'BebasNeuePro', 'Arial Black', sans-serif);
    font-size: clamp(48px, 5vw, 70px);
    line-height: .95;
    letter-spacing: .02em;
    text-transform: uppercase;
  }

  > p {
    margin: 22px 0 0;
    color: rgba(255, 255, 255, .7);
    font-size: 16px;
    line-height: 1.65;
  }
`;

const CommunityMedia = styled.div`
  min-height: 610px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    object-position: 68% center;
  }

  @media (max-width: 820px) {
    min-height: 280px;
    order: -1;
  }
`;

const PartnershipSection = styled.section`
  position: relative;
  width: min(1400px, calc(100% - 32px));
  min-height: 680px;
  margin: 0 auto 20px;
  overflow: hidden;
  border-radius: 26px;
  display: flex;
  align-items: center;
  color: #fff;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
    background: linear-gradient(90deg, rgba(18, 19, 17, .94), rgba(18, 19, 17, .74) 48%, rgba(18, 19, 17, .3));
  }

  @media (max-width: 760px) {
    width: 100%;
    min-height: 0;
    border-radius: 0;
    display: block;
    background: #20221f;

    &::after { display: none; }
  }
`;

const PartnershipImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;

  @media (max-width: 760px) {
    position: relative;
    height: 260px;
    object-position: 67% center;
  }
`;

const PartnershipContent = styled.div`
  position: relative;
  z-index: 2;
  width: min(1240px, calc(100% - 96px));
  margin: 0 auto;
  padding: 80px 0;

  @media (max-width: 760px) {
    width: 100%;
    padding: 48px 20px 58px;
  }
`;

const PartnershipCopy = styled.div`
  max-width: 760px;

  h2 {
    max-width: 720px;
    margin: 0;
    font-family: var(--font-heading, 'BebasNeuePro', 'Arial Black', sans-serif);
    font-size: clamp(52px, 6vw, 82px);
    line-height: .92;
    letter-spacing: .02em;
    text-transform: uppercase;
  }

  > p {
    max-width: 620px;
    margin: 22px 0 0;
    color: rgba(255, 255, 255, .76);
    font-size: 17px;
    line-height: 1.65;
  }
`;

const PartnerChoices = styled.div`
  max-width: 760px;
  margin-top: 32px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const PartnerChoice = styled.div`
  padding: 22px;
  border: 1px solid rgba(255, 255, 255, .18);
  border-radius: 14px;
  background: rgba(255, 255, 255, .94);
  color: #252621;

  strong {
    display: block;
    margin-bottom: 8px;
    font-size: 16px;
  }

  span {
    display: block;
    color: #62665f;
    font-size: 13px;
    line-height: 1.55;
  }
`;

const FinalCta = styled.section`
  padding: clamp(76px, 9vw, 132px) 24px;
  background: #171916;
  color: #fff;
  text-align: center;
`;

const FinalCtaInner = styled.div`
  max-width: 920px;
  margin: 0 auto;

  h2 {
    margin: 0;
    font-family: var(--font-heading, 'BebasNeuePro', 'Arial Black', sans-serif);
    font-size: clamp(56px, 7vw, 96px);
    line-height: .92;
    letter-spacing: .02em;
    text-transform: uppercase;
  }

  p {
    max-width: 560px;
    margin: 24px auto 0;
    color: rgba(255, 255, 255, .68);
    font-size: 16px;
    line-height: 1.6;
  }

  ${ActionRow} { justify-content: center; }
`;

const Home = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const { addToFavorites, removeFromFavorites, isFavorite } = useFavorites();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 15000);
    let isMounted = true;

    const fetchProducts = async () => {
      try {
        const response = await fetch('/api/products?limit=6', {
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal
        });

        if (!response.ok) throw new Error('Ошибка загрузки товаров');

        const data = await response.json();
        const productsList = data?.data?.products;
        const productsArray = Array.isArray(productsList) ? productsList : [];

        const formattedProducts = productsArray.map(product => ({
          id: product.id,
          name: product.name,
          price: parseFloat(product.price) || 0,
          oldPrice: product.compare_price ? parseFloat(product.compare_price) : null,
          code: product.sku || '',
          rating: product.avg_rating ? Math.round(parseFloat(product.avg_rating)) : 0,
          reviewsCount: parseInt(product.reviews_count, 10) || 0,
          description: product.short_description || product.description || '',
          image: product.featured_image_url || getImageUrl(product.featured_image) || '/nofoto.png',
          images: product.images
            ? product.images.map(image => image.startsWith('http') ? image : getImageUrl(image))
            : [],
          category: product.category_slug || '',
          brand: product.brand || product.brand_name || 'Lagom Life',
          inStock: (product.total_stock_quantity || 0) > 0,
          total_stock_quantity: parseInt(product.total_stock_quantity || 0, 10),
          isNew: false
        }));

        if (isMounted) setProducts(formattedProducts);
      } catch (error) {
        if (!isMounted) return;
        console.error('Ошибка загрузки товаров:', error);
        setProducts([]);
        setLoadError(error.name === 'AbortError' ? 'Каталог отвечает дольше обычного' : 'Не удалось загрузить товары');
      } finally {
        window.clearTimeout(timeoutId);
        if (isMounted) setLoading(false);
      }
    };

    fetchProducts();

    return () => {
      isMounted = false;
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, []);

  const handleToggleFavorite = async (productId, isProductFavorite) => {
    if (!isAuthenticated()) {
      alert('Необходима авторизация для добавления в избранное');
      return;
    }

    try {
      if (isProductFavorite) {
        await removeFromFavorites(productId);
      } else {
        await addToFavorites(productId);
      }
    } catch (error) {
      console.error('Ошибка при изменении избранного:', error);
      alert('Не удалось изменить избранное');
    }
  };

  return (
    <HomeContainer>
      <Slider />

      <TrustSection aria-label="Преимущества Lagom Life">
        <TrustGrid>
          {TRUST_ITEMS.map(item => {
            const Icon = item.icon;
            return (
              <TrustItem key={item.title}>
                <Icon aria-hidden="true" />
                <div>
                  <strong>{item.title}</strong>
                  <span>{item.text}</span>
                </div>
              </TrustItem>
            );
          })}
        </TrustGrid>
      </TrustSection>

      <Section aria-labelledby="home-goals-title">
        <SectionHead>
          <SectionIntro>
            <Eyebrow>С чего начать</Eyebrow>
            <SectionTitle id="home-goals-title">Продукты под разные задачи</SectionTitle>
            <SectionSubtitle>
              Спортивное питание, витамины и функциональные напитки для тренировок, восстановления и повседневного рациона.
            </SectionSubtitle>
          </SectionIntro>
          <InlineLink to="/catalog">Весь каталог <FiArrowRight aria-hidden="true" /></InlineLink>
        </SectionHead>

        <GoalGrid>
          {GOALS.map(goal => {
            const Icon = goal.icon;
            return (
              <GoalCard key={goal.title} to="/catalog" $tone={goal.tone} $number={goal.number}>
                <GoalIcon><Icon aria-hidden="true" /></GoalIcon>
                <GoalCopy>
                  <h3>{goal.title}</h3>
                  <p>{goal.description}</p>
                </GoalCopy>
                <GoalArrow $tone={goal.tone}><FiArrowRight aria-hidden="true" /></GoalArrow>
              </GoalCard>
            );
          })}
        </GoalGrid>
      </Section>

      <SurfaceSection>
        <Section aria-labelledby="home-products-title">
          <SectionHead>
            <SectionIntro>
              <Eyebrow>Выбор покупателей</Eyebrow>
              <SectionTitle id="home-products-title">Популярные товары</SectionTitle>
              <SectionSubtitle>
                Продукты Lagom Life, которые чаще всего выбирают для тренировок, восстановления и активного дня.
              </SectionSubtitle>
            </SectionIntro>
            <InlineLink to="/catalog">Смотреть все <FiArrowRight aria-hidden="true" /></InlineLink>
          </SectionHead>

          <ProductGrid aria-live="polite" aria-busy={loading}>
            {loading
              ? Array.from({ length: 6 }).map((_, index) => <ProductSkeleton key={index} aria-hidden="true" />)
              : products.map(product => (
                <HomeProductCard
                  key={product.id}
                  product={product}
                  isFavorite={isFavorite(product.id)}
                  onToggleFavorite={handleToggleFavorite}
                />
              ))}

            {!loading && loadError ? <Notice>{loadError}. Полный ассортимент доступен в каталоге.</Notice> : null}
            {!loading && !loadError && products.length === 0
              ? <Notice>Популярные товары скоро появятся. Весь ассортимент уже доступен в каталоге.</Notice>
              : null}
          </ProductGrid>
        </Section>
      </SurfaceSection>

      <StorySection aria-labelledby="home-story-title">
        <StoryImage src="/home/production-quality.png" alt="Контроль продукции Lagom Life на производстве" loading="lazy" />
        <StoryContent>
          <StoryCopy>
            <LightEyebrow>Производство в Самарской области</LightEyebrow>
            <h2 id="home-story-title">Собственное производство — контроль на каждом этапе</h2>
            <p>
              Спортивное питание Lagom Life выпускается в Самарской области. Проверяем сырьё, технологический процесс, фасовку и готовые партии. Документы на продукцию опубликованы на сайте.
            </p>
            <StoryFacts>
              {STORY_FACTS.map(fact => (
                <StoryFact key={fact}><FiCheckCircle aria-hidden="true" />{fact}</StoryFact>
              ))}
            </StoryFacts>
            <ActionRow>
              <ActionLink to="/certificates" $primary>Сертификаты <FiArrowRight aria-hidden="true" /></ActionLink>
              <ActionLink to="/catalog">Каталог продукции</ActionLink>
            </ActionRow>
          </StoryCopy>
        </StoryContent>
      </StorySection>

      <Section aria-labelledby="home-process-title">
        <SectionHead>
          <SectionIntro>
            <Eyebrow>Ассортимент Lagom Life</Eyebrow>
            <SectionTitle id="home-process-title">Больше, чем спортивный протеин</SectionTitle>
            <SectionSubtitle>
              В линейке — спортивные смеси, питательные коктейли, витаминно-минеральные комплексы и функциональные напитки.
            </SectionSubtitle>
          </SectionIntro>
          <InlineLink to="/catalog">Весь каталог <FiArrowRight aria-hidden="true" /></InlineLink>
        </SectionHead>

        <ProcessGrid>
          {PRODUCT_LINES.map(line => {
            const Icon = line.icon;
            return (
              <ProcessStep key={line.number} $number={line.number}>
                <Icon aria-hidden="true" />
                <h3>{line.title}</h3>
                <p>{line.text}</p>
              </ProcessStep>
            );
          })}
        </ProcessGrid>
      </Section>

      <TasteSection aria-labelledby="home-taste-title">
        <TasteImage src="/home/protein-taste-v3.png" alt="Покупательница пьёт коктейль Lagom Life на кухне" loading="lazy" />
        <TasteContent>
          <TasteCopy>
            <LightEyebrow>Вкусовая линейка</LightEyebrow>
            <h2 id="home-taste-title">Протеин со вкусом маффина, тирамису и шоколадного мороженого</h2>
            <p>
              Среди других вкусов — фруктовый микс, банан, клубника и тропические фрукты. Линейка включает сывороточные, соевые, казеиновые и многокомпонентные смеси.
            </p>
            <FlavorList aria-label="Популярные вкусы">
              <Flavor>Маффин</Flavor>
              <Flavor>Шоколадное мороженое</Flavor>
              <Flavor>Тирамису</Flavor>
            </FlavorList>
            <ActionRow>
              <ActionLink to="/catalog" $primary>Выбрать свой вкус <FiArrowRight aria-hidden="true" /></ActionLink>
            </ActionRow>
          </TasteCopy>
        </TasteContent>
      </TasteSection>

      <CommunityPanel aria-labelledby="home-experts-title">
        <CommunityCopy>
          <LightEyebrow>Подборки экспертов</LightEyebrow>
          <h2 id="home-experts-title">Готовые комплексы от тренеров и нутрициологов</h2>
          <p>
            Тренеры и нутрициологи собирают комплексы для тренировочного режима, восстановления и повседневного рациона. На их страницах — опыт, статьи и состав каждой подборки.
          </p>
          <ActionRow>
            <ActionLink to="/experts" $primary>Смотреть экспертов <FiArrowRight aria-hidden="true" /></ActionLink>
            <ActionLink to="/become-expert">Стать экспертом</ActionLink>
          </ActionRow>
        </CommunityCopy>
        <CommunityMedia>
          <img src="/slider/hero-experts-v3.png" alt="Нутрициолог обсуждает состав Lagom Life с посетителем" loading="lazy" />
        </CommunityMedia>
      </CommunityPanel>

      <PartnershipSection aria-labelledby="home-partner-title">
        <PartnershipImage src="/slider/hero-partnership-v3.png" alt="Партнёры выбирают продукты Lagom Life" loading="lazy" />
        <PartnershipContent>
          <PartnershipCopy>
            <LightEyebrow>Партнёрская программа</LightEyebrow>
            <h2 id="home-partner-title">Партнёрство без собственного склада</h2>
            <p>
              У каждого партнёра есть персональная ссылка. Покупатель оформляет заказ на сайте, FoodStar организует сборку и доставку, а оплаченный заказ учитывается в партнёрской программе.
            </p>
            <PartnerChoices>
              <PartnerChoice>
                <strong>Персональная ссылка</strong>
                <span>Заказы по ней учитываются в вашем аккаунте.</span>
              </PartnerChoice>
              <PartnerChoice>
                <strong>Сборка и доставка — на FoodStar</strong>
                <span>Без закупки товара для перепродажи и приёма оплаты от покупателей.</span>
              </PartnerChoice>
            </PartnerChoices>
            <ActionRow>
              <ActionLink to="/referral" $primary>Узнать о партнёрстве <FiArrowRight aria-hidden="true" /></ActionLink>
            </ActionRow>
          </PartnershipCopy>
        </PartnershipContent>
      </PartnershipSection>

      <FinalCta>
        <FinalCtaInner>
          <FiAward size={30} aria-hidden="true" />
          <h2>Спортивное питание Lagom Life для разных задач</h2>
          <p>Протеины, гейнеры, аминокислоты, витамины и травяные напитки — от тренировочного режима до повседневного рациона.</p>
          <ActionRow>
            <ActionLink to="/catalog" $primary>Открыть каталог <FiArrowRight aria-hidden="true" /></ActionLink>
          </ActionRow>
        </FinalCtaInner>
      </FinalCta>
    </HomeContainer>
  );
};

export default Home;
